/* eslint-disable no-unused-vars */
import React, { useEffect, useContext, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, Outlet, useLocation } from 'react-router-dom';
import axios from 'util/Api';
import { isEmpty } from 'lodash';
import { ConfigContext } from 'store/context';
import { Layout } from 'antd';
import Sidebar from 'components/Sidebar/index';
import AppHeader from 'pages/App/Header';
import Loading from 'components/Loading';
import { CollapseSidebar } from 'store/actions/ConfigAction';
import useProfile from 'hooks/data/useProfile';
import useTenant from 'hooks/data/useTenant';
import { SUBSCRIPTION_STATUS } from 'constants/Common';
import SignIn from '../Login/SignIn';
import Confirmation from '../Login/Confirmation';
import ForgotPassword from '../Login/ForgotPassword';
import ResetPassword from '../Login/ResetPassword';
import SignUp from '../Login/SignUp';
import Invitation from '../Login/Invitation';
import RejectInvite from '../Login/RejectInvite';
import { useAuth } from '../../hooks/useAuth';

const Page404 = lazy(() => import('pages/Login/404'));
const Page500 = lazy(() => import('pages/Login/500'));
const Home = lazy(() => import('pages/DashboardPage'));
const Profile = lazy(() => import('pages/Profile'));
const POS = lazy(() => import('pages/POS'));
const POSRegister = lazy(() => import('pages/POSRegister'));
const POSOrderDetail = lazy(() => import('pages/POSOrderDetail'));
const POSSettingForm = lazy(() => import('pages/POSSettingForm'));
const POSSettingList = lazy(() => import('pages/POSSettingList'));
const QualityControl = lazy(() => import('pages/QualityControl'));
const QuantityControlForm = lazy(() => import('pages/QualityControl/QualityControlForm'));
const CustomerComplaint = lazy(() => import('pages/CustomerComplaint'));
const CustomerComplaintForm = lazy(() => import('pages/CustomerComplaint/CustomerComplaintForm'));
const PatientTracking = lazy(() => import('pages/PatientTracking/PatientTrackingList'));
const InventoryValuation = lazy(() => import('pages/Report/InventoryValuation'));
const Setting = lazy(() => import('pages/SettingPage'));
const StoreSettingPage = lazy(() => import('pages/StoreSettingPage'));
const GeneralSetting = lazy(() => import('pages/GeneralSetting'));
const PurchaseOrderDetail = lazy(() => import('pages/PurchaseOrderDetail'));
const PurchaseOrderList = lazy(() => import('pages/PurchaseOrderList'));
const SettingStaff = lazy(() => import('pages/StaffPage'));
const AddNewStaff = lazy(() => import('pages/StaffPage/Form'));
const EditStaff = lazy(() => import('pages/StaffPage/Form'));
const Subscription = lazy(() => import('pages/SubscriptionPage'));
const CustomerList = lazy(() => import('pages/CustomerList'));
const CustomerForm = lazy(() => import('pages/CustomerForm'));
const CustomerGroupList = lazy(() => import('pages/CustomerGroupList'));
const ProductList = lazy(() => import('pages/ProductList'));
const ProductForm = lazy(() => import('pages/ProductForm'));
const CategoryList = lazy(() => import('pages/CategoryList'));
const UOMList = lazy(() => import('pages/UOMList'));
const TagList = lazy(() => import('pages/TagList'));
const VendorList = lazy(() => import('pages/VendorList'));
const VendorForm = lazy(() => import('pages/VendorForm'));
const POSOrderList = lazy(() => import('pages/POSOrderList'));
const ScrapList = lazy(() => import('pages/ScrapList'));
const ScrapForm = lazy(() => import('pages/ScrapForm'));
const InventoryList = lazy(() => import('pages/InventoryList'));
const InventoryForm = lazy(() => import('pages/InventoryForm'));
const TaxSetting = lazy(() => import('pages/TaxSetting'));
const EInvoiceSetting = lazy(() => import('pages/EInvoiceSetting'));
const SetupStore = lazy(() => import('pages/SetupStore'));
const PrescriptionTemplate = lazy(() => import('pages/PrescriptionTemplate'));
const PrescriptionTemplateForm = lazy(() => import('pages/PrescriptionTemplate/PrescriptionTemplateForm'));
const SpecializationList = lazy(() => import('pages/SpecializationList'));
const Doctor = lazy(() => import('pages/Doctor'));
const DoctorForm = lazy(() => import('pages/Doctor/Form'));
const EInvoice = lazy(() => import('pages/EInvoice'));
const EInvoiceForm = lazy(() => import('pages/EInvoice/EInvoiceForm'));

const { Content } = Layout;
const routeNoSidebar = ['/pos', '/setup-store', '/404', '/500'];
const routeForError = ['/404', '/500'];

function WaitingComponent(Component, propsCustom) {
  return (
    <Suspense fallback={<Loading />}>
      <AppHeader {...propsCustom} />
      <Component {...propsCustom} />
    </Suspense>
  );
}

function WaitingComponentPOS(Component, propsCustom) {
  return (
    <Suspense fallback={<Loading />}>
      <Component {...propsCustom} />
    </Suspense>
  );
}

function renderSidebar() {
  if (routeNoSidebar.includes(window.location.pathname)) {
    return null;
  }
  return <Sidebar />;
}

function RequireAuth(props) {
  const { isAuthenticated } = props;
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  return (
    <>
      {routeForError.includes(window.location.pathname) ? (
        <Outlet />
      ) : (
        <Layout className="omi-app-layout">
          {renderSidebar()}
          <Layout className={`bg-grey-200  ${window.location.pathname === '/pos' ? 'pos' : ''}`}>
            <Content>
              <Outlet />
            </Content>
          </Layout>
        </Layout>
      )}
    </>
  );
}

function SubscriptionRequired({ subscription, loadingTenant }) {
  if (window.location.pathname === '/settings/subscriptions' || loadingTenant) {
    return <Outlet />;
  }
  // const isActive = [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL].includes(subscription.status_name);
  // if (!isActive) {
  //   return <Navigate to="/settings/subscriptions" state={{ showPaidModal: true }} />;
  // }
  return (
    <>
      <Outlet />
    </>
  );
}

export default function () {
  const { isAuthenticated, token, initURL, user } = useAuth();

  const location = useLocation();
  const { mutate } = useProfile();
  const {
    tenant,
    mutate: mutateTenant,
    loading: loadingTenant,
  } = useTenant({ revalidateOnFocus: false, revalidateOnMount: false });

  const {
    ConfigState: { statusCollapse: status },
    ConfigDispatch,
  } = useContext(ConfigContext);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && !isEmpty(user)) {
      mutate();
      mutateTenant();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (routeNoSidebar.includes(window.location.pathname)) {
      ConfigDispatch(CollapseSidebar(false));
    } else {
      ConfigDispatch(CollapseSidebar(true));
    }
  }, [location.pathname]);

  if (window.location.pathname === '/') {
    if (token === null && isEmpty(user)) {
      return <Navigate to="/login" />;
    }
    if (initURL === '' || initURL === '/' || initURL === '/login') {
      return <Navigate to="/dashboard" />;
    }
    return <Navigate to={initURL} />;
  }

  return (
    <Routes>
      <Route path="/">
        <Route element={<SignIn />} />
        <Route path="Login" element={<SignIn />} />
        <Route path="confirm" element={<Confirmation />} />
        <Route path="forgot-password" element={<ForgotPassword />} />

        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="signup" element={<SignUp />} />
        <Route path="invitations" element={<Invitation />} />
        <Route path="invitations/reject" element={<RejectInvite />} />
        {/* <RestrictedRoute isAuthenticated={isAuthenticated} element={<MainApp />} /> */}
        <Route path="*" element={<RequireAuth isAuthenticated={isAuthenticated} />}>
          <Route path="404" element={<Page404 />} />
          <Route path="500" element={<Page500 />} />
          <Route path="setup-store" element={<SetupStore />} />

          <Route path="settings" element={<Outlet />}>
            <Route index element={WaitingComponent(Setting, { status })} />
            <Route
              path="subscriptions"
              element={WaitingComponent(Subscription, {
                status,
                redirect: { name: 'Cấu hình', link: '/settings' },
              })}
            />
          </Route>
          <Route
            path="*"
            element={<SubscriptionRequired subscription={tenant?.subscription || {}} loadingTenant={loadingTenant} />}
          >
            <Route path="dashboard" element={WaitingComponent(Home)} />
            <Route path="profile" element={WaitingComponent(Profile)} />
            <Route path="customer-groups" element={WaitingComponent(CustomerGroupList, { status })} />
            <Route path="pos-registers" element={WaitingComponent(POSRegister, { status })} />
            <Route path="report/inventory-valuations" element={WaitingComponent(InventoryValuation, { status })} />
            <Route path="categories" element={WaitingComponent(CategoryList, { status })} />
            <Route path="uoms" element={WaitingComponent(UOMList, { status })} />
            <Route path="tags" element={WaitingComponent(TagList, { status })} />
            <Route path="pos" element={WaitingComponentPOS(POS)} />
            <Route path="specializations" element={WaitingComponent(SpecializationList)} />
            {/* Prescription Template Page */}

            {/* Doctors Page */}
            <Route path="doctors" element={<Outlet />}>
              <Route index element={WaitingComponent(Doctor, { status })} />
              <Route
                path="create"
                element={WaitingComponent(DoctorForm, {
                  status,
                  redirect: { name: 'Bác sĩ', link: '/doctors' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(DoctorForm, {
                  status,
                  redirect: { name: 'Bác sĩ', link: '/doctors' },
                })}
              />
            </Route>

            {/* Customers Page */}
            <Route path="customers" element={<Outlet />}>
              <Route index element={WaitingComponent(CustomerList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(CustomerForm, {
                  status,
                  redirect: { name: 'Khách hàng', link: '/customers' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(CustomerForm, {
                  status,
                  redirect: { name: 'Khách hàng', link: '/customers' },
                })}
              />
            </Route>

            {/* EInvoices Page */}
            <Route path="einvoices" element={<Outlet />}>
              <Route index element={WaitingComponent(EInvoice, { status })} />

              <Route
                path=":id/edit"
                element={WaitingComponent(EInvoiceForm, {
                  status,
                  redirect: { name: 'Hoá đơn điện tử', link: '/einvoices' },
                })}
              />
            </Route>

            {/* Inventories Page */}
            <Route path="inventories" element={<Outlet />}>
              <Route index element={WaitingComponent(InventoryList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(InventoryForm, {
                  status,
                  redirect: { name: 'Kiểm kê', link: '/inventories' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(InventoryForm, {
                  status,
                  redirect: { name: 'Kiểm kê', link: '/inventories' },
                })}
              />
            </Route>

            {/* Scraps Page */}
            <Route path="scraps" element={<Outlet />}>
              <Route index element={WaitingComponent(ScrapList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(ScrapForm, { status, redirect: { name: 'Xuất hỏng', link: '/scraps' } })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(ScrapForm, { status, redirect: { name: 'Xuất hỏng', link: '/scraps' } })}
              />
            </Route>

            {/* Pos Orders Page */}
            <Route path="pos-orders" element={<Outlet />}>
              <Route index element={WaitingComponent(POSOrderList, { status })} />
              <Route
                path=":id/show"
                element={WaitingComponent(POSOrderDetail, {
                  status,
                  redirect: { name: 'Lịch sử đơn hàng', link: '/pos-orders' },
                })}
              />
            </Route>

            {/* Pos Settings Page */}
            <Route path="pos-settings" element={<Outlet />}>
              <Route index element={WaitingComponent(POSSettingList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thêm bộ phím tắt', link: '/pos-settings' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thiết lập điểm bán', link: '/pos-settings' },
                })}
              />
            </Route>
            {/* Purchase Orders Page */}
            <Route path="purchase-orders" element={<Outlet />}>
              <Route index element={WaitingComponent(PurchaseOrderList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(PurchaseOrderDetail, {
                  status,
                  redirect: { name: 'Nhập hàng', link: '/purchase-orders' },
                })}
              />
              <Route
                path=":id/show"
                element={WaitingComponent(PurchaseOrderDetail, {
                  status,
                  redirect: { name: 'Nhập hàng', link: '/purchase-orders' },
                })}
              />
            </Route>

            {/* Pos Settings Page */}
            <Route path="pos-settings" element={WaitingComponent(POSSettingList, { status })}>
              <Route
                path="pos-settings/:id/edit"
                element={WaitingComponent(POSSettingForm, {
                  status,
                  redirect: { name: 'Thiết lập điểm bán', link: '/pos-settings' },
                })}
              />
            </Route>

            {/* Quality Control Page */}
            <Route path="quality-controls" element={<Outlet />}>
              <Route
                index
                element={WaitingComponent(QualityControl, {
                  status,
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(QuantityControlForm, {
                  status,
                  redirect: { name: 'Danh sách quản lý chất lượng', link: '/quality-controls' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(QuantityControlForm, {
                  status,
                  redirect: { name: 'Danh sách quản lý chất lượng', link: '/quality-controls' },
                })}
              />
            </Route>

            {/* Customer Complaint Page */}
            <Route path="customer-complaints" element={<Outlet />}>
              <Route
                index
                element={WaitingComponent(CustomerComplaint, {
                  status,
                })}
              />
              <Route
                path="create"
                element={WaitingComponent(CustomerComplaintForm, {
                  status,
                  redirect: { name: 'Phiếu theo dõi xử lý khiếu nại', link: '/customer-complaints' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(CustomerComplaintForm, {
                  status,
                  redirect: { name: 'Phiếu the dõi xử lý khiếu nại', link: '/customer-complaints' },
                })}
              />
            </Route>

            {/* Patient Tracking Page */}
            <Route
              path="patient-tracking"
              element={WaitingComponent(PatientTracking, {
                status,
              })}
            />

            {/* Settings Page */}
            <Route path="settings" element={<Outlet />}>
              <Route index element={WaitingComponent(Setting, { status })} />
              <Route
                path="store"
                element={WaitingComponent(StoreSettingPage, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route path="prescription-templates" element={<Outlet />}>
                <Route
                  index
                  element={WaitingComponent(PrescriptionTemplate, {
                    status,
                    redirect: { name: 'Cấu hình', link: '/settings' },
                  })}
                />
                <Route
                  path="create"
                  element={WaitingComponent(PrescriptionTemplateForm, {
                    status,
                    redirect: { name: 'Danh sách đơn mẫu', link: '/settings/prescription-templates' },
                  })}
                />
                <Route
                  path=":id/edit"
                  element={WaitingComponent(PrescriptionTemplateForm, {
                    status,
                    redirect: { name: 'Danh sách đơn mẫu', link: '/settings/prescription-templates' },
                  })}
                />
              </Route>
              <Route
                path="general"
                element={WaitingComponent(GeneralSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route
                path="taxes"
                element={WaitingComponent(TaxSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
              <Route path="staffs" element={<Outlet />}>
                <Route
                  index
                  element={WaitingComponent(SettingStaff, {
                    status,
                    redirect: { name: 'Cấu hình', link: '/settings' },
                  })}
                />
                <Route
                  path="create"
                  element={WaitingComponent(AddNewStaff, {
                    status,
                    redirect: { name: 'Nhân viên', link: '/settings/staffs' },
                  })}
                />
                <Route
                  path=":id/edit"
                  element={WaitingComponent(EditStaff, {
                    status,
                    redirect: { name: 'Nhân viên', link: '/settings/staffs' },
                  })}
                />
              </Route>
              <Route
                path="einvoices"
                element={WaitingComponent(EInvoiceSetting, {
                  status,
                  redirect: { name: 'Cấu hình', link: '/settings' },
                })}
              />
            </Route>

            {/* Products Page */}
            <Route path="products" element={<Outlet />}>
              <Route index element={WaitingComponent(ProductList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(ProductForm, {
                  status,
                  redirect: { name: 'Sản phẩm', link: '/products' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(ProductForm, {
                  status,
                  redirect: { name: 'Sản phẩm', link: '/products' },
                })}
              />
            </Route>

            {/* Vendors Page */}
            <Route path="vendors" element={<Outlet />}>
              <Route index element={WaitingComponent(VendorList, { status })} />
              <Route
                path="create"
                element={WaitingComponent(VendorForm, {
                  status,
                  redirect: { name: 'Nhà cung cấp', link: '/vendors' },
                })}
              />
              <Route
                path=":id/edit"
                element={WaitingComponent(VendorForm, {
                  status,
                  redirect: { name: 'Nhà cung cấp', link: '/vendors' },
                })}
              />
            </Route>

            {/* 404 Page */}
            <Route path="*" element={<Navigate replace to="/404" />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
